import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Repeat, Clock } from "lucide-react";
import { toLocalDateStr } from "./pulseUtils";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM
const HOUR_HEIGHT = 72;

function formatHour(h) {
  const period = h >= 12 ? "PM" : "AM";
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:00 ${period}`;
}

/**
 * Compute column layout for overlapping events.
 * Returns array of { event, top, height, col, totalCols }
 */
function layoutEvents(events) {
  if (!events.length) return [];

  const items = events.map(event => {
    const start = new Date(event.start_datetime);
    const end = new Date(event.end_datetime);
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    return {
      event,
      startHour,
      endHour: Math.max(endHour, startHour + 0.5), // min 30 min visual
      top: (startHour - 7) * HOUR_HEIGHT,
      height: Math.max((endHour - startHour) * HOUR_HEIGHT, HOUR_HEIGHT * 0.5),
    };
  }).sort((a, b) => a.startHour - b.startHour || a.endHour - b.endHour);

  // Group into overlapping clusters
  const clusters = [];
  let currentCluster = [items[0]];

  for (let i = 1; i < items.length; i++) {
    const clusterEnd = Math.max(...currentCluster.map(c => c.endHour));
    if (items[i].startHour < clusterEnd) {
      currentCluster.push(items[i]);
    } else {
      clusters.push(currentCluster);
      currentCluster = [items[i]];
    }
  }
  clusters.push(currentCluster);

  // Assign columns within each cluster
  const result = [];
  for (const cluster of clusters) {
    const columns = [];
    for (const item of cluster) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        const lastInCol = columns[c][columns[c].length - 1];
        if (item.startHour >= lastInCol.endHour) {
          columns[c].push(item);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([item]);
      }
    }
    const totalCols = columns.length;
    columns.forEach((col, colIdx) => {
      col.forEach(item => {
        result.push({ ...item, col: colIdx, totalCols });
      });
    });
  }

  return result;
}

export const PulseDayView = ({ currentDate, selectedDate, events, tasks, onNavigateDay, onGoToToday, onEventClick, onSlotClick, onEventDrop }) => {
  const { t } = useTranslation();
  const [dragOverHour, setDragOverHour] = useState(null);

  const dayEvents = useMemo(() => {
    const dateStr = toLocalDateStr(selectedDate);
    return events
      .filter(e => toLocalDateStr(new Date(e.start_datetime)) === dateStr)
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [events, selectedDate]);

  const layoutItems = useMemo(() => layoutEvents(dayEvents), [dayEvents]);

  const dayTasks = useMemo(() => {
    const dateStr = toLocalDateStr(selectedDate);
    return tasks.filter(t => {
      if (!t.due_datetime) return false;
      return toLocalDateStr(new Date(t.due_datetime)) === dateStr;
    });
  }, [tasks, selectedDate]);

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const nowHour = new Date().getHours();
  const nowMinute = new Date().getMinutes();

  const dayLabel = selectedDate.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric"
  });

  const handleSlotClick = (hour) => {
    const d = new Date(selectedDate);
    d.setHours(hour, 0, 0, 0);
    if (onSlotClick) onSlotClick(d);
  };

  const handleDragStart = (e, event) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", JSON.stringify({
      eventId: event.id,
      startDatetime: event.start_datetime,
      endDatetime: event.end_datetime,
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverHour(hour);
  };

  const handleDrop = (e, hour) => {
    e.preventDefault();
    setDragOverHour(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!data.eventId) return;
      const oldStart = new Date(data.startDatetime);
      const oldEnd = new Date(data.endDatetime);
      const duration = oldEnd - oldStart;
      const newStart = new Date(selectedDate);
      newStart.setHours(hour, 0, 0, 0);
      const newEnd = new Date(newStart.getTime() + duration);
      if (onEventDrop) onEventDrop(data.eventId, newStart, newEnd);
    } catch {}
  };

  const isRecurring = (event) => event.id?.includes("_r");

  return (
    <div className="space-y-4" data-testid="day-view">
      {/* Day Controls */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onNavigateDay(-1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onGoToToday} className="rounded-sm h-8 px-3">{t("pulsePlanner.today")}</Button>
          <Button variant="outline" size="sm" onClick={() => onNavigateDay(1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="font-display text-base font-semibold text-slate-900 uppercase tracking-tight">{dayLabel}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Time Grid */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-y-auto max-h-[560px]">
            <div className="grid grid-cols-[64px_1fr] relative">
              {/* Hour Labels */}
              <div className="relative">
                {HOURS.map(hour => (
                  <div key={hour} style={{ height: HOUR_HEIGHT }} className="flex items-start justify-end pr-3 border-b border-slate-50">
                    <span className="text-xs text-slate-400 font-mono -mt-2">{formatHour(hour)}</span>
                  </div>
                ))}
              </div>

              {/* Event Column */}
              <div className="relative border-l border-slate-200">
                {/* Hour slots (clickable background) */}
                {HOURS.map(hour => (
                  <div
                    key={hour}
                    style={{ height: HOUR_HEIGHT }}
                    className={`border-b border-slate-50 cursor-pointer transition-colors ${
                      dragOverHour === hour ? "bg-indigo-100/50" : "hover:bg-slate-50/80"
                    }`}
                    onClick={() => handleSlotClick(hour)}
                    onDragOver={(e) => handleDragOver(e, hour)}
                    onDragLeave={() => setDragOverHour(null)}
                    onDrop={(e) => handleDrop(e, hour)}
                  >
                    <div className="border-b border-dashed border-slate-50" style={{ marginTop: HOUR_HEIGHT / 2 - 1 }} />
                  </div>
                ))}

                {/* Current time indicator */}
                {isToday && nowHour >= 7 && nowHour <= 21 && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: (nowHour - 7 + nowMinute / 60) * HOUR_HEIGHT }}
                  >
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 -ml-1.5 border-2 border-white shadow" />
                      <div className="flex-1 h-[2px] bg-red-500" />
                    </div>
                  </div>
                )}

                {/* Events — laid out in columns */}
                {layoutItems.map(({ event, top, height, col, totalCols }) => {
                  const isRec = isRecurring(event);
                  if (top < 0 || top > HOURS.length * HOUR_HEIGHT) return null;
                  const start = new Date(event.start_datetime);
                  const end = new Date(event.end_datetime);
                  const timeStr = `${start.getHours().toString().padStart(2, "0")}:${start.getMinutes().toString().padStart(2, "0")} – ${end.getHours().toString().padStart(2, "0")}:${end.getMinutes().toString().padStart(2, "0")}`;

                  const colWidth = `calc((100% - 8px) / ${totalCols})`;
                  const colLeft = `calc(4px + (100% - 8px) / ${totalCols} * ${col})`;

                  return (
                    <div
                      key={event.id}
                      draggable={!isRec}
                      onDragStart={!isRec ? (e) => handleDragStart(e, event) : undefined}
                      onClick={(e) => { e.stopPropagation(); if (onEventClick) onEventClick(event); }}
                      className={`absolute z-10 rounded-sm px-2 py-1.5 overflow-hidden border-l-3 border-l-indigo-500 bg-indigo-50 hover:bg-indigo-100 shadow-sm transition-colors ${
                        !isRec ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                      }`}
                      style={{
                        top,
                        height: Math.max(height, 28),
                        left: colLeft,
                        width: colWidth,
                      }}
                      title={`${event.title}${isRec ? " (recurring)" : " - drag to reschedule"}`}
                    >
                      <p className="text-xs font-semibold text-indigo-800 truncate leading-tight">{event.title}</p>
                      {height > 30 && (
                        <p className="text-[10px] text-indigo-500 font-mono flex items-center gap-1 mt-0.5">
                          <Clock className="w-2.5 h-2.5" />{timeStr}
                        </p>
                      )}
                      {height > 55 && event.location && (
                        <p className="text-[10px] text-indigo-400 truncate flex items-center gap-1 mt-0.5">
                          <MapPin className="w-2.5 h-2.5" />{event.location}
                        </p>
                      )}
                      {isRec && <Repeat className="w-2.5 h-2.5 text-indigo-400 absolute top-1 right-1" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Side Panel: Tasks */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 self-start">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-blue-600" />
            {t("pulsePlanner.tasks")} ({dayTasks.length})
          </h3>
          {dayTasks.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">{t("pulsePlanner.noTasksForDay")}</p>
          ) : (
            <div className="space-y-2">
              {dayTasks.map(task => {
                const due = task.due_datetime ? new Date(task.due_datetime) : null;
                const priorityColor = task.priority === "high" ? "border-l-red-500 bg-red-50" :
                  task.priority === "medium" ? "border-l-amber-500 bg-amber-50" :
                  "border-l-emerald-500 bg-emerald-50";
                return (
                  <div key={task.id} className={`p-2.5 rounded-sm border-l-3 ${priorityColor} ${task.status === "completed" ? "opacity-50" : ""}`}>
                    <p className={`text-sm font-medium ${task.status === "completed" ? "line-through text-slate-400" : "text-slate-800"}`}>{task.title}</p>
                    {due && (
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {t("pulsePlanner.due")}: {due.getHours().toString().padStart(2, "0")}:{due.getMinutes().toString().padStart(2, "0")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
